import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { billingService } from '../services/billingService';
import { getApiErrorMessage } from '../utils/getApiErrorMessage';

const BILLING_CACHE_TTL_MS = 2 * 60 * 1000;

const isFresh = (timestamp, maxAgeMs = BILLING_CACHE_TTL_MS) => Date.now() - timestamp < maxAgeMs;

const INITIAL_FETCH_ERRORS = { cards: null, history: null, profile: null };

/** Some endpoints return HTTP 200 with { status: "error", ... }; treat as failure. */
function ensureBillingResponseOk(response, contextLabel) {
  const payload = response?.data;
  const statusVal = payload?.status;
  if (typeof statusVal === 'string' && statusVal.toLowerCase() === 'error') {
    const err = new Error(payload?.message || `${contextLabel} failed`);
    err.response = { status: response?.status || 400, data: payload };
    throw err;
  }
}

// Phase 6: Billing server-state cache in Zustand with stale-while-revalidate behavior.
export const useBillingStore = create(
  devtools(
    persist(
      (set, get) => ({
        cards: [],
        billingHistory: [],
        profile: null,
        lastFetched: {
          cards: 0,
          history: 0,
          profile: 0,
        },
        loading: {
          cards: false,
          history: false,
          profile: false,
        },
        fetchErrors: { ...INITIAL_FETCH_ERRORS },
        clearBillingFetchErrors: () =>
          set(() => ({ fetchErrors: { ...INITIAL_FETCH_ERRORS } }), false, 'billing/clearFetchErrors'),

        fetchCards: async ({ force = false, background = true } = {}) => {
          const state = get();
          const hasCards = state.cards.length > 0;
          if (!force && hasCards && isFresh(state.lastFetched.cards)) {
            return state.cards;
          }

          if (!background || !hasCards) {
            set((prev) => ({ loading: { ...prev.loading, cards: true } }), false, 'billing/fetchCards:start');
          }

          try {
            const response = await billingService.getCards();
            ensureBillingResponseOk(response, 'Cards');
            const cardsPayload = Array.isArray(response?.data?.cards)
              ? response.data.cards
              : Array.isArray(response?.data?.data?.cards)
                ? response.data.data.cards
                : [];
            const cards = Array.isArray(cardsPayload) ? cardsPayload : [];
            set(
              (prev) => ({
                cards,
                lastFetched: { ...prev.lastFetched, cards: Date.now() },
                loading: { ...prev.loading, cards: false },
                fetchErrors: { ...prev.fetchErrors, cards: null },
              }),
              false,
              'billing/fetchCards:success'
            );
            return cards;
          } catch (error) {
            const message = getApiErrorMessage(error, 'Could not load payment methods.');
            set(
              (prev) => ({
                cards: [],
                loading: { ...prev.loading, cards: false },
                fetchErrors: { ...prev.fetchErrors, cards: message },
              }),
              false,
              'billing/fetchCards:error'
            );
            throw error;
          }
        },
        fetchBillingHistory: async ({ force = false, background = true } = {}) => {
          const state = get();
          const hasHistory = state.billingHistory.length > 0;
          if (!force && hasHistory && isFresh(state.lastFetched.history)) {
            return state.billingHistory;
          }

          if (!background || !hasHistory) {
            set(
              (prev) => ({ loading: { ...prev.loading, history: true } }),
              false,
              'billing/fetchHistory:start'
            );
          }

          try {
            const response = await billingService.getBillingHistory();
            ensureBillingResponseOk(response, 'Billing history');
            const history = Array.isArray(response?.data?.data?.data)
              ? response.data.data.data
              : Array.isArray(response?.data?.data)
                ? response.data.data
                : Array.isArray(response?.data?.history)
                  ? response.data.history
                  : [];
            set(
              (prev) => ({
                billingHistory: history,
                lastFetched: { ...prev.lastFetched, history: Date.now() },
                loading: { ...prev.loading, history: false },
                fetchErrors: { ...prev.fetchErrors, history: null },
              }),
              false,
              'billing/fetchHistory:success'
            );
            return history;
          } catch (error) {
            const message = getApiErrorMessage(error, 'Could not load billing history.');
            set(
              (prev) => ({
                billingHistory: [],
                loading: { ...prev.loading, history: false },
                fetchErrors: { ...prev.fetchErrors, history: message },
              }),
              false,
              'billing/fetchHistory:error'
            );
            throw error;
          }
        },
        fetchProfile: async ({ force = false, background = true } = {}) => {
          const state = get();
          if (!force && state.profile && isFresh(state.lastFetched.profile)) {
            return state.profile;
          }

          if (!background || !state.profile) {
            set((prev) => ({ loading: { ...prev.loading, profile: true } }), false, 'billing/fetchProfile:start');
          }

          try {
            const response = await billingService.getProfile();
            ensureBillingResponseOk(response, 'Wallet profile');
            const profile = response?.data?.data || response?.data?.profile || response?.data || null;
            set(
              (prev) => ({
                profile,
                lastFetched: { ...prev.lastFetched, profile: Date.now() },
                loading: { ...prev.loading, profile: false },
                fetchErrors: { ...prev.fetchErrors, profile: null },
              }),
              false,
              'billing/fetchProfile:success'
            );
            return profile;
          } catch (error) {
            const message = getApiErrorMessage(error, 'Could not load wallet profile.');
            set(
              (prev) => ({
                profile: null,
                loading: { ...prev.loading, profile: false },
                fetchErrors: { ...prev.fetchErrors, profile: message },
              }),
              false,
              'billing/fetchProfile:error'
            );
            throw error;
          }
        },
        hydrateBillingData: async () => {
          const state = get();
          await Promise.allSettled([
            state.fetchCards({ background: true }),
            state.fetchProfile({ background: true }),
            state.fetchBillingHistory({ background: true }),
          ]);
        },
      }),
      {
        name: 'billing-store',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          cards: state.cards,
          billingHistory: state.billingHistory,
          profile: state.profile,
          lastFetched: state.lastFetched,
        }),
      }
    ),
    { name: 'billing-store-devtools' }
  )
);
